import React, { useState, useEffect } from 'react';
import { SearchIcon, EyeIcon, CheckIcon, XIcon, TrashIcon } from '@heroicons/react/outline';
import { toastService } from '../../../services/toast';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { ordersApi } from '../../../services/api/orders.api';

const BankTransfers = () => {
  const { hasPermission } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('all');
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [bankTransfers, setBankTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    const fetchBankTransfers = async () => {
      try {
        // جلب طلبات الدورات
        const courseResponse = await ordersApi.getAllOrders('course');
        const courseTransfers = courseResponse
          .filter(order => order.payment_method === 'bank-transfer')
          .map(order => ({
            id: order.id,
            trainee: order.userName,
            itemTitle: order.items[0]?.courseTitle || 'دورة غير معروفة',
            type: 'course',
            date: order.created_at,
            receipt: order.receipt,
            status: order.status
          }));

        // جلب طلبات الكتب
        const bookResponse = await ordersApi.getAllOrders('book');
        const bookTransfers = bookResponse
          .filter(order => order.payment_method === 'bank-transfer')
          .map(order => ({
            id: order.id,
            trainee: order.userName,
            itemTitle: order.items[0]?.bookTitle || 'كتاب غير معروف',
            type: 'book',
            date: order.created_at,
            receipt: order.receipt,
            status: order.status
          }));

        // دمج الطلبات
        setBankTransfers([...courseTransfers, ...bookTransfers]);
      } catch (error) {
        console.error('Error fetching transfers:', error);
        toastService.error('فشل في جلب بيانات التحويلات');
      } finally {
        setLoading(false);
      }
    };

    fetchBankTransfers();
  }, []);

  // فلترة التحويلات حسب البحث والتاريخ
  const filterTransfers = (transfers) => {
    return transfers.filter(transfer => {
      const matchesSearch = searchQuery === '' || 
        (transfer.trainee && transfer.trainee.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (transfer.itemTitle && transfer.itemTitle.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (transfer.id && transfer.id.toString().includes(searchQuery));

      if (!matchesSearch) return false;

      const transferDate = new Date(transfer.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      switch (selectedDate) {
        case 'today':
          return transferDate >= today;
        case 'yesterday':
          return transferDate >= yesterday && transferDate < today;
        case 'week':
          return transferDate >= weekAgo;
        case 'month':
          return transferDate >= monthAgo;
        default:
          return true;
      }
    });
  };

  const filteredPendingTransfers = filterTransfers(bankTransfers.filter(transfer => transfer.status === 'pending'));
  const filteredReviewedTransfers = filterTransfers(bankTransfers.filter(transfer => transfer.status && transfer.status !== 'pending'));

  // بحث ذكي: يبدأ بعد حرفين مع تأخير 500ms
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // لا حاجة لفetch لأن البيانات محلية، فقط إعادة تصفية
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewReceipt = (receipt) => {
    setSelectedReceipt(receipt);
  };

  const handleApprove = async (id, type) => {
    try {
      setIsLoading(true);
      const transfer = bankTransfers.find(t => t.id === id && t.type === type);
      if (!transfer) {
        throw new Error('لم يتم العثور على التحويل');
      }

      await ordersApi.updateOrderStatus(id, 'accepted', type);
      
      // تحديث الحالة في الواجهة
      const updatedTransfer = { ...transfer, status: 'accepted' };
      setBankTransfers(prev => prev.map(t => t.id === id && t.type === type ? updatedTransfer : t));
      
      toastService.success('تم قبول التحويل بنجاح');
    } catch (error) {
      console.error('Error approving transfer:', error);
      toastService.error('حدث خطأ أثناء قبول التحويل');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (id, type) => {
    try {
      setIsLoading(true);
      const transfer = bankTransfers.find(t => t.id === id && t.type === type);
      if (!transfer) {
        throw new Error('لم يتم العثور على التحويل');
      }

      await ordersApi.updateOrderStatus(id, 'rejected', type);
      
      // تحديث الحالة في الواجهة
      const updatedTransfer = { ...transfer, status: 'rejected' };
      setBankTransfers(prev => prev.map(t => t.id === id && t.type === type ? updatedTransfer : t));
      
      toastService.success('تم رفض التحويل بنجاح');
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      toastService.error('حدث خطأ أثناء رفض التحويل');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id, type) => {
    setSelectedTransfer({ id, type });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTransfer) return;
    
    setIsLoading(true);
    try {
      // حذف من الباك إند
      await ordersApi.deleteOrder(selectedTransfer.id, selectedTransfer.type);
      // ثم تحديث الحالة المحلية
      setBankTransfers(prev => prev.filter(t => !(t.id === selectedTransfer.id && t.type === selectedTransfer.type)));
      toastService.success('تم حذف التحويل البنكي بنجاح');
    } catch (error) {
      console.error('Error deleting bank transfer:', error);
      toastService.error('حدث خطأ أثناء حذف التحويل البنكي');
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedTransfer(null);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getCurrentItems = (items) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const currentItems = getCurrentItems(
    activeTab === 'pending' ? filteredPendingTransfers : filteredReviewedTransfers
  );

  const totalPages = Math.ceil(
    (activeTab === 'pending' ? filteredPendingTransfers.length : filteredReviewedTransfers.length) / itemsPerPage
  );

  return (
    <>
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* بطاقة البحث والفلترة */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
            {/* رأس البطاقة */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">البحث والفلترة</h1>
            </div>

            {/* قسم الفلتر والبحث */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    البحث
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="ابحث باسم المتدرب أو الدورة..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="input-base pr-10"
                    />
                    <SearchIcon className="absolute left-3 top-2 h-5 w-5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    تاريخ التحويل
                  </label>
                  <select
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="input-base"
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* بطاقة جدول التحويلات */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
            {/* رأس البطاقة */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">
                {activeTab === 'pending' ? 'تحت المراجعة' : 'تمت المراجعة'}
              </h1>
              <div className="flex space-x-4 space-x-reverse">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md min-w-[120px] ${
                    activeTab === 'pending'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  تحت المراجعة ({filteredPendingTransfers.length})
                </button>
                <button
                  onClick={() => setActiveTab('reviewed')}
                  className={`flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md min-w-[120px] ${
                    activeTab === 'reviewed'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  تمت المراجعة ({filteredReviewedTransfers.length})
                </button>
              </div>
            </div>

            {/* الجدول */}
            <div className="w-full" key={activeTab}>
              <div className="align-middle inline-block min-w-full">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          المتدرب
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          المنتج
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          التاريخ
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          الإيصال
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {activeTab === 'pending' ? 'الإجراءات' : 'الحالة'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentItems.map((transfer) => (
                        <tr key={transfer.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {transfer.trainee}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {transfer.itemTitle}
                            <span className="mr-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              {transfer.type === 'course' ? 'دورة' : 'كتاب'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatDate(transfer.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <button 
                              onClick={() => handleViewReceipt(transfer.receipt)}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center justify-center">
                            {activeTab === 'pending' ? (
                              <div className="flex items-center space-x-2 space-x-reverse">
                                <div className="relative group">
                                  {hasPermission && hasPermission('verify_bank_transfers') && (
                                    <button
                                      onClick={() => handleApprove(transfer.id, transfer.type)}
                                      className="text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                  <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -top-8 right-1/2 translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                    قبول التحويل
                                  </div>
                                </div>
                                <div className="relative group">
                                  {hasPermission && hasPermission('verify_bank_transfers') && (
                                    <button
                                      onClick={() => handleReject(transfer.id, transfer.type)}
                                      className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                                    >
                                      <XIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                  <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -top-8 right-1/2 translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                    رفض التحويل
                                  </div>
                                </div>
                                <div className="relative group">
                                  {hasPermission && hasPermission('delete_bank_transfers') && (
                                    <button
                                      onClick={() => handleDelete(transfer.id, transfer.type)}
                                      className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                  <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -top-8 right-1/2 translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                    حذف التحويل
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                transfer.status === 'accepted'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                  : transfer.status === 'rejected'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                              }`}>
                                {transfer.status === 'accepted' ? 'تم القبول' : 
                                 transfer.status === 'rejected' ? 'تم الرفض' : 
                                 'تحت المراجعة'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && currentItems.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400'
                    }`}
                  >
                    السابق
                  </button>
                  <div className="flex items-center gap-2">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          currentPage === index + 1
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400'
                    }`}
                  >
                    التالي
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* مودال عرض الإيصال */}
      {selectedReceipt && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer"
          onClick={() => setSelectedReceipt(null)}
        >
          <div className="relative max-w-3xl mx-4">
            <button
              onClick={() => setSelectedReceipt(null)}
              className="absolute top-2 left-2 text-white bg-black bg-opacity-50 rounded-full p-1 hover:bg-opacity-75 focus:outline-none"
            >
              <XIcon className="h-6 w-6" />
            </button>
            <img 
              src={selectedReceipt} 
              alt="إيصال التحويل" 
              className="w-full rounded-lg"
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </div>
      )}

      {/* مودال الحذف */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsDeleteModalOpen(false);
            setSelectedTransfer(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        itemType="التحويل البنكي"
        itemTitle={selectedTransfer?.trainee}
        isLoading={isLoading}
      />
    </>
  );
};

export default BankTransfers;
